import { NotAvailable } from "./components/NotAvailable/NotAvailable";
import { OnlineStock } from "./components/OnlineStock/OnlineStock";
import { AddToBasketEsiWrapper } from "./components/AddToBasketEsiWrapper/AddToBasketEsiWrapper";
import styles from "./AddToBasket.module.scss";
import { OutOfStockExperienceInfo } from "./components/OutOfStockExperienceInfo/OutOfStockExperienceInfo";
import classNames from "classnames/bind";
import { AgeRestrictionMessage } from "./components/AgeRestrictionMessage/AgeRestrictionMessage";
import { CustomerNotifiableEvents } from "../../types/prodcatBaseTypes";
import { useEffect } from "react";
import { VariantAttributes } from "./components/VariantAttributes/VariantAttributes";
import { VariantPrice } from "../../types/types";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { ALPHA__Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/ALPHA/Drawer";

const cx = classNames.bind(styles);

type Props = {
  /**
   * A string representing the product ID from the Product Information Management system.
   */
  pimProductId: string;
  /**
   * A boolean indicating whether the product has a single SKU.
   */
  isSingleSkuProduct: boolean;
  /**
   * A string representing the display code for the store stock.
   */
  storeStockDisplayCode: string;
  /**
   * A string representing the SKU ID for the store stock.
   */
  storeStockSkuId: string;
  /**
   * A boolean indicating whether the product is a future release.
   */
  isFutureRelease: boolean;
  /**
   * A boolean indicating whether the product is available for pre-order.
   */
  isPreOrderable: boolean;
  /**
   * A string representing the SKU ID of the product.
   */
  skuId: string;
  /**
   * A string representing the variant ID of the product.
   */
  variantId: string;
  /**
   * A string representing the title of the product.
   */
  title: string;
  /**
   * A string representing the price of the product.
   */
  price: string;
  /**
   * An object of type `CustomerNotifiableEvents` representing the events that the customer can be notified about.
   */
  customerNotifiableEvents: CustomerNotifiableEvents;
  /**
   * A boolean indicating whether the product is only available in store.
   */
  isInStoreOnly: boolean;
  /**
   * A boolean indicating whether the product is available to order.
   */
  availableToOrder: boolean;
  /**
   * A boolean indicating whether the product is available to pre-order.
   */
  availableToPreOrder: boolean;
  /**
   * A boolean indicating whether a valid size has been selected for the product.
   */
  hasAvailableVariantsMatchingColour: boolean;
  /**
   * A string representing a direct message from the supplier, or `undefined`.
   */
  supplierDirectMessage: string | undefined;
  /**
   * A string representing the base URL for the notification API.
   */
  notificationApiBaseUrl: string;
  /**
   * A string representing the API key for the notification API.
   */
  notificationApiKey: string;
  /**
   * A string representing the base URL for the stock API.
   */
  stockApiBaseUrl: string;
  /**
   * A string representing the API key for the stock API.
   */
  stockApiKey: string;
  /**
   * A string representing the API key for the stock API.
   */
  storeStockApiKey: string;
  /**
   * A string representing the URL for the store stock script.
   */
  storeStockScriptUrl: string;
  /**
   * A string representing the URL for the store stock modal information api.
   */
  storeStockModalApiUrl: string;
  /**
   * A number representing the minimum age to purchase the product, or `null` or `undefined` if there is no minimum age requirement.
   */
  minimumAge: number | null | undefined;
  /**
   * A boolean indicating whether or not the selected variant can be added to basket
   */
  isSelected: boolean;

  recsPlacementId: string;

  oosPlacementId: string;
  /**
   * A boolean indicating whether or not the product has any variants with stock
   */
  hasStock: boolean;
  /**
   * A string of the selected variant dimensions or undefined if there are none
   */
  dimensions: string | null | undefined;
  /**
   * Variant Price or Price Range string if applicable
   */
  priceDisplay: VariantPrice | string;
  /**
   * A string Size of the selected variant
   */
  size: string | null | undefined;
  /**
   * Colour of the selected variant
   */
  colour: string | null | undefined;
  /**
   * Toggle to allow showing out of stock experience on multi variant
   */
  showOosExperienceForMultiVariant: boolean;
  /**
   * A boolean representing if the product is sold by the metre
   */
  isPerMetre: boolean;
  /**
   * A number representing the minimum length of a product sold by the metre
   */
  perMetreMinimumLength: number | null | undefined;
  /**
   * Toggle to enable/ disable the add to basked button if useracknowledgment is checked/ unchecked
   */
  userAcknowledgementChecked: boolean;
  /**
   * A boolean to decide if the user confirmation is required to decide enable/disable the a2b
   */
  userAcknowledgementConfirmationRequired: boolean;

  /**
   * Toggle to enable/ disable express checkout
   */
  expressCheckoutEnabled: boolean;

  /**
   * Toggle to enable/ disable express checkout
   */
  hideOosRecs?: boolean;
  /**
   * Explicitly hide add to basket button
   */
  addToBasketButtonEnabled?: boolean;
  /**
   * Show Ship From Store Drawer
   */
  shipFromStore: boolean;
};

export const AddToBasketUI = ({
  pimProductId,
  isSingleSkuProduct,
  storeStockDisplayCode,
  storeStockSkuId,
  isFutureRelease,
  isPreOrderable,
  skuId,
  variantId,
  title,
  price,
  customerNotifiableEvents,
  isInStoreOnly,
  availableToOrder,
  availableToPreOrder,
  supplierDirectMessage,
  notificationApiBaseUrl,
  notificationApiKey,
  stockApiBaseUrl,
  stockApiKey,
  storeStockApiKey,
  storeStockScriptUrl,
  storeStockModalApiUrl,
  minimumAge,
  isSelected,
  recsPlacementId,
  oosPlacementId,
  hasStock,
  dimensions,
  priceDisplay,
  size,
  colour,
  hasAvailableVariantsMatchingColour,
  showOosExperienceForMultiVariant,
  isPerMetre,
  perMetreMinimumLength,
  userAcknowledgementChecked,
  userAcknowledgementConfirmationRequired,
  expressCheckoutEnabled,
  hideOosRecs = false,
  addToBasketButtonEnabled = true,
  shipFromStore,
}: Props) => {
  const showOutOfStockExperienceInfo =
    !hasStock &&
    !isFutureRelease &&
    (isSingleSkuProduct || showOosExperienceForMultiVariant);

  const hideAddToBasketEsi =
    isSingleSkuProduct &&
    !availableToOrder &&
    !isFutureRelease &&
    !isPreOrderable;

  const isNotifiable =
    !availableToOrder &&
    (customerNotifiableEvents.backInStock ||
      customerNotifiableEvents.onRelease);

  const shouldHideAddToBasket = userAcknowledgementConfirmationRequired
    ? !userAcknowledgementChecked
    : isSelected && (isInStoreOnly || isNotifiable || isFutureRelease);

  const shouldEnableAddToBasket = isSelected
    ? availableToOrder
    : hasAvailableVariantsMatchingColour;

  useEffect(() => {
    const eventDetail = {
      detail: {
        skuId: skuId,
        variantId: isSelected ? variantId : "",
        title: title,
        price: price,
        enabled: String(shouldEnableAddToBasket),
        hidden: String(shouldHideAddToBasket),
        childItems: [],
      },
    };

    const event = new CustomEvent(
      "ADD_TO_BASKET_ESI:VARIANT_UPDATED",
      eventDetail,
    );

    dispatchEvent(event);
  }, [
    availableToOrder,
    customerNotifiableEvents,
    price,
    isSelected,
    skuId,
    title,
    variantId,
    userAcknowledgementChecked,
  ]);

  return (
    <div
      data-testid="add-to-basket-ui"
      className={cx(styles.wrapper, {
        outOfStock: showOutOfStockExperienceInfo,
      })}
    >
      {showOutOfStockExperienceInfo ? (
        <>
          <OutOfStockExperienceInfo
            storeStockTitle={title}
            storeStockDisplayCode={storeStockDisplayCode}
            storeStockSkuId={storeStockSkuId}
            isFutureRelease={isFutureRelease}
            isPreOrderable={isPreOrderable}
            storeStockScriptUrl={storeStockScriptUrl}
            stockApiBaseUrl={stockApiBaseUrl}
            stockApiKey={stockApiKey}
            storeStockApiKey={storeStockApiKey}
            storeStockModalApiUrl={storeStockModalApiUrl}
            isInStoreOnly={isInStoreOnly}
            isVariantSelected={isSelected}
          />
        </>
      ) : null}
      <VariantAttributes
        hasAnyStock={hasStock}
        dimensions={dimensions}
        priceDisplay={priceDisplay}
        size={size}
        colour={colour}
        isPerMetre={isPerMetre}
        perMetreMinimumLength={perMetreMinimumLength}
      />
      <OnlineStock
        skuId={skuId}
        isInStoreOnly={isInStoreOnly}
        isAvailableToOrderOrPreOrder={availableToOrder || availableToPreOrder}
        supplierDirectMessage={supplierDirectMessage}
        variantIsSelected={isSelected}
        stockApiKey={stockApiKey}
        stockApiBaseUrl={stockApiBaseUrl}
      />
      {availableToOrder && <AgeRestrictionMessage minimumAge={minimumAge} />}
      {!shouldEnableAddToBasket && (
        <NotAvailable
          pimProductId={pimProductId}
          skuId={skuId}
          customerNotifiableEvents={customerNotifiableEvents}
          notificationsApiBaseUrl={notificationApiBaseUrl}
          notificationsApiKey={notificationApiKey}
          oosPlacementId={oosPlacementId}
          isSelected={isSelected}
          hideOosRecs={hideOosRecs}
        />
      )}
      {!hideAddToBasketEsi && (
        <AddToBasketEsiWrapper
          pimProductId={pimProductId}
          skuId={skuId}
          variantId={variantId}
          title={title}
          price={price}
          availableToOrder={availableToOrder}
          customerNotifiableEvents={customerNotifiableEvents}
          hideAddToBasket={shouldHideAddToBasket}
          sendVariantIdToBasket={isSelected}
          shouldEnableAddToBasket={shouldEnableAddToBasket}
          recsPlacementId={recsPlacementId}
          expressCheckoutEnabled={expressCheckoutEnabled}
          showAddToBasketButton={addToBasketButtonEnabled}
          shipFromStore={shipFromStore || false}
          stockApiBaseUrl={stockApiBaseUrl}
          stockApiKey={stockApiKey}
        />
      )}
    </div>
  );
};
